import React, { Fragment, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getLegendItems } from '@constants/config';
import { paper } from '@constants/mui-theme';
import {
  getEnabledEntities,
  agencyTypesSelector,
  categoriesSelector,
  getEnabledEntityTypes
} from '@selectors/entities-selector';
import { layerTypesSelector } from '@selectors/layers-selector';
import LegendAvatar from '@shared/legend-avatar';
import {
  getAgencyIcon,
  getCategoryIcon,
  getDefaultEntityIcon,
  getLayerIcon
} from '@utils/icon-utils';
import './legend.scss';

const getCategories = state => {
  let categories = [];
  const entityTypes = getEnabledEntityTypes(state);
  entityTypes.forEach(entity => {
    categories = [...categories, ...categoriesSelector(state, { type: entity })];
  });
  return categories;
};

const Legend = () => {
  const legendOpen = useSelector(state => state.map.legendOpen);
  const enabledEntities = useSelector(getEnabledEntities);
  const agencyTypes = useSelector(agencyTypesSelector);
  const layerTypes = useSelector(layerTypesSelector);
  const categories = useSelector(getCategories);
  const entityTypes = useSelector(state => state.dataTypes.map_type);

  const hasItems = [
    ...Object.values(enabledEntities).flat(),
    ...agencyTypes,
    ...layerTypes,
    ...categories
  ].length > 0;

  const buildItems = useCallback((item, list, iconLegend, display = 'name') => {
    if (list.length === 0) {
      return null;
    }
    const filteredlist = [];
    list.forEach(listItem => {
      const filteredItem = filteredlist.filter(fi => fi.name === listItem.name);
      if (filteredItem.length === 0) {
        filteredlist.push(listItem);
      }
    });
    return (
      <div styleName="map-legend-list-group">
        <div styleName="map-legend-title">
          {item.title}
        </div>
        {item.subtitle && (
          <div styleName="map-legend-subtitle">
            {item.subtitle}
          </div>
        )}
        {filteredlist.map(entity => {
          const icon = iconLegend(entity);
          return (
            <div styleName="map-legend-list-item" key={entity.id || entity.name}>
              {item.type !== 'layer' &&
                <LegendAvatar src={icon} />
              }
              {item.type === 'layer' && <img src={icon} alt="" styleName="icon" />}
              <div styleName="text">
                {entity[display]}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, []);

  const renderAgencies = useCallback(item => {
    const entityType = item.entities[0];
    let filteredAgencyTypes = [];
    if (enabledEntities[entityType] && enabledEntities[entityType].length > 0) {
      filteredAgencyTypes = agencyTypes.filter(type => enabledEntities[entityType].some(entity => entity.agencyTypeId === type.id));
    }
    return buildItems(item, filteredAgencyTypes, getAgencyIcon);
  }, [agencyTypes, buildItems, enabledEntities]);

  const renderCategories = useCallback(item => {
    const iconLegend = category => getCategoryIcon(item.id, category.id);
    // Filter categories to return only the values for the current selected category type.
    const categoryTypeId = item['category-type'];
    const currentCategories = categories.filter(category => category.type === categoryTypeId);
    const entityType = item.entities[0];
    let entityCategories = [];
    if (enabledEntities[entityType] && enabledEntities[entityType].length > 0) {
      entityCategories = [...new Set(enabledEntities[entityType]
        .filter(entity => typeof entity.category_dict !== 'undefined')
        .map(entity => entity.category_dict)
        .flat(2)
        .map(category => category.id))];
    }
    const filteredCurrentCategories = currentCategories.filter(category => entityCategories.some(ec => ec === category.id));

    return buildItems(item, filteredCurrentCategories, iconLegend);
  }, [buildItems, categories, enabledEntities]);

  const renderCityLayers = useCallback(item => {
    const iconLegend = layerType => getLayerIcon(layerType.icon).url;
    return buildItems(item, layerTypes, iconLegend, 'label');
  }, [buildItems, layerTypes]);

  const renderEntityIcon = useCallback(item => {
    const entityType = item.id;
    if (enabledEntities[entityType] && enabledEntities[entityType].length > 0) {
      const list = [{
        id: item.id,
        name: ''
      }];
      const iconLegend = () => getCategoryIcon(
        item.id,
        getDefaultEntityIcon(
          { type_name: item.id },
          entityTypes
        )
      );
      return buildItems(item, list, iconLegend);
    }
    return null;
  }, [buildItems, enabledEntities, entityTypes]);

  const renderGroup = useCallback(item => {
    if (item.type === 'agency') {
      return renderAgencies(item);
    }
    if (item.type === 'entity') {
      return renderEntityIcon(item);
    }
    if (item.type === 'entity-category') {
      return renderCategories(item);
    }
    if (item.type === 'layer') {
      return renderCityLayers(item);
    }
    return null;
  }, [renderAgencies, renderCategories, renderCityLayers, renderEntityIcon]);

  const renderEmpty = () => (
    <div styleName="map-legend-list-group">
      <div styleName="map-legend-empty">
        No records shown
      </div>
    </div>
  );

  const renderLegends = () => {
    if (!hasItems) {
      return renderEmpty();
    }
    const items = getLegendItems();

    return items.map((item, index) => {
      const node = renderGroup(item);
      if (node) {
        return (
          <Fragment key={item.id}>
            {index > 0 &&
              <div styleName="map-legend-list-separator" />
            }
            { node }
          </Fragment>
        );
      }
      return null;
    });
  };

  if (!legendOpen) {
    return null;
  }

  return (
    <div styleName="map-legend-container" style={paper}>
      <div styleName="map-legend-list">
        {renderLegends()}
      </div>
    </div>
  );
};

export default Legend;
